import {HttpErrorResponse} from "@angular/common/http";
import {Component, inject} from "@angular/core";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {Subject, takeUntil} from "rxjs";
import {OpportunityService} from "../../../../core/services/opportunity.service";
import {PageConfigurationService} from "../../../../core/services/page-configuration.service";
import {DispatchOrderResponse, OrderDispatchDetails} from "../../../../core/types/dispatch.types";
import {PageConfiguration, PageConfigurationResponse} from "../../../../core/types/page-configuration.types";
import {EntityResponse} from "../../../../core/types/types";
import {CurrencyPipe, NgFor, NgIf} from "@angular/common";
import {getFilenameFromHeaders} from "../../../../core/utils/file.utils";
import {ModalService} from "../../../../core/services/modal.service";
import {humanizeText} from "../../../../core/utils/string-utils";
import {signatureNameValidator} from "../../../../core/validators/signature-name.validator";
import {SignatureInputComponent} from "../../../shared/signature-input/signature-input.component";

import {FormValueSyncDirective} from "../../../../core/directives/form-value-sync.directive";
import {FormValueSyncRegistry} from "../../../../core/directives/form-value-sync.registry";
@Component({
    selector: "app-dispatch-sheet",
    standalone: true,
    imports: [NgIf, NgFor, CurrencyPipe, ReactiveFormsModule, FormsModule, SignatureInputComponent, FormValueSyncDirective],
    templateUrl: "./dispatch-sheet.component.html"
})
export class DispatchSheetComponent {
    private _formValueSync = inject(FormValueSyncRegistry);
    signatureForm: FormGroup | null = null;

    dispatchDetails: OrderDispatchDetails | null = null;
    configuration: PageConfiguration | null = null;

    dispatchSigned: boolean = false;

    signingInProgress: boolean = false;
    downloadingPdf: boolean = false;

    private _opportunityService = inject(OpportunityService);
    private _configurationService = inject(PageConfigurationService);
    private _modalService = inject(ModalService);

    private _destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.fetchConfiguration();
        this.fetchDispatchOrderDetails();
    }

    createForm(): void {
        this.signatureForm = new FormGroup({
            signedBy: new FormControl(null, [Validators.required, signatureNameValidator()]),
            ip: new FormControl({ value: null, disabled: true }),
            signedOn: new FormControl({ value: null, disabled: true }),
            agree: new FormControl(false, [Validators.requiredTrue]),
        });
    }

    fetchConfiguration(): void {
        this._configurationService.pageConfiguration$
            .pipe(takeUntil(this._destroy$))
            .subscribe((response: PageConfigurationResponse | null) => {
                if (response && response.success) {
                    this.configuration = response.data!;
                }
            });
    }

    fetchDispatchOrderDetails(): void {
        this._opportunityService
            .getDispatchOrderDetails()
            .pipe(takeUntil(this._destroy$))
            .subscribe((response: EntityResponse<OrderDispatchDetails>) => {
                this.dispatchDetails = response.data;
                this.createForm();
                if (!!this.dispatchDetails.loadAcceptedAt) {
                    this.dispatchSigned = true;
                    this.signatureForm?.patchValue({
                        agree: true,
                        ip: this.dispatchDetails.clientIp,
                        signedBy: this.dispatchDetails.signedBy,
                        signedOn: this.dispatchDetails.loadAcceptedAt,
                    });
                    this.signatureForm?.disable();
                } else {
                    this.signatureForm?.patchValue({
                        ip: this.dispatchDetails.clientIp,
                        signedOn: new Date(),
                    });
                }
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    submit(): void {
        this._formValueSync.syncAll();

        if (this.signatureForm?.invalid) {
            this.signatureForm?.markAllAsTouched();
            return;
        }

        this.signingInProgress = true;

        const payload = {
            signedBy: this.signatureControls.signedBy.value,
            ip: this.signatureControls.ip.value,
            signedOnTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        this._opportunityService.signDispatchOrder(payload).subscribe({
            next: (response: EntityResponse<DispatchOrderResponse>) => {
                this.dispatchDetails!.loadAcceptedAt = response.data.loadAcceptedAt;
                this.dispatchDetails!.loadAcceptedAtTimeZone = response.data.loadAcceptedAtTimeZone;
                this.dispatchDetails!.signedBy = this.signatureControls.signedBy.value;
                this.dispatchSigned = true;
                this.signatureForm?.disable();
                this.signingInProgress = false;
            },
            error: (error: HttpErrorResponse) => {
                this.signingInProgress = false;
                this.onError(error);
            },
        });
    }

    humanizePaymentKey(key: string): string {
        return key.replace(/([A-Z])/g, " $1").trim();
    }

    onError(error: HttpErrorResponse): void {
        this._modalService.showError(error.error?.title, error.error?.message);
    }

    downloadPdf(): void {
        this.downloadingPdf = true;

        this._opportunityService.getDispatchSheet().subscribe({
            next: (response) => {
                const blob = response.body;
                const filename = getFilenameFromHeaders(
                    response.headers,
                    `Order_${this.dispatchDetails?.id}_Dispatch_Sheet.pdf`);

                if (blob) {
                    const url = window.URL.createObjectURL(blob);

                    const a = document.createElement('a');
                    a.style.display = 'none';
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);

                    a.click();

                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                }
                this.downloadingPdf = false;
            },
            error: (err) => {
                this.downloadingPdf = false;
                this.onError(err);
            }
        });
    }

    humanizeText(input: string): string {
        return humanizeText(input);
    }

    get signatureControls() {
        return {
            signedBy: this.signatureForm?.get("signedBy") as FormControl,
            ip: this.signatureForm?.get("ip") as FormControl,
            signedOn: this.signatureForm?.get("signedOn") as FormControl,
            agree: this.signatureForm?.get("agree") as FormControl,
        };
    }

}
